import _ from "lodash";
import toast from "react-hot-toast";

// constants
import LocalStrings from "@language";
import { postRequest, deleteRequest, patchRequest } from "@network";
import { INSTANCE_URL } from "@constant";
import {
  REVIEWER_API_ERROR_CODES,
  REVIEWER_API_PAYLOAD_STRUCTURES,
  REVIEWER_RESPONSE_TYPES,
  REVIEWER_REDUCER_STRUCTURES,
  PROPOSAL_SUBMISSION_TYPES,
} from "@reviewerconstant";
import { format } from "date-fns";
import { utcToZonedTime } from "date-fns-tz";

export const REVIEWER_LOGIN = "REVIEWER_LOGIN";
export const REVIEWER_LOGOUT = "REVIEWER_LOGOUT";
export const REVIEWER_RESPONSES = "REVIEWER_RESPONSES";
export const REVIEWER_ALL_CRITERIA = "REVIEWER_ALL_CRITERIA";
export const REVIEWER_ALL_REVIEW = "REVIEWER_ALL_REVIEW";

export const reviewerLoginAction = (
  session,
  payload = { email: "", password: "" },
  extra = { eventData: {} },
  callback
) => {
  // Invalid credential check
  if (_.isEmpty(payload?.email) || _.isEmpty(payload?.password))
    return (dispatch) => {
      toast.error(LocalStrings.static.kWrongCred);
      callback({
        success: 0,
        data: null,
        error: LocalStrings.static.kWrongCred,
      });
    };

  let postUrl = `${INSTANCE_URL}/api/v2/user/session`;
  let postBody = _.cloneDeep(REVIEWER_API_PAYLOAD_STRUCTURES.login);
  postBody.email = payload?.email;
  postBody.password = payload?.password;

  return (dispatch) => {
    postRequest(
      postUrl,
      postBody,
      session,
      dispatch,
      (res) => {
        if (res?.error) {
          toast.error(LocalStrings.static.kWrongCred);
          callback({
            success: 0,
            data: null,
            error: LocalStrings.static.kWrongCred,
          });
        } else {
          let payloadObj = {
            isLoggedIn: true,
            ...res,
          };

          if (!_.isEmpty(extra?.eventData)) {
            dispatch(
              fetchReviewerData(
                session,
                {
                  dfId: res?.id,
                  eventid: extra?.eventData?.eventid,
                },
                (reviewerRes) => {
                  if (reviewerRes?.success) {
                    if (reviewerRes?.userFound) {
                      payloadObj = {
                        ...payloadObj,
                        ...reviewerRes?.data,
                        eventid: extra?.eventData?.eventid,
                      };
                      dispatch({
                        type: REVIEWER_LOGIN,
                        payload: payloadObj,
                      });
                      callback({ success: 1, data: payloadObj, error: null });
                    } else {
                      toast.error(LocalStrings.static.kReviewerNotFound);
                      callback({
                        success: 1,
                        data: {},
                        error: LocalStrings.static.kReviewerNotFound,
                      });
                    }
                  } else {
                    toast.error(LocalStrings.static.kReviewerNotFound);
                    callback({
                      success: 0,
                      data: null,
                      error: LocalStrings.static.kReviewerNotFound,
                    });
                  }
                }
              )
            );
          } else {
            callback({
              success: 0,
              data: null,
              error: LocalStrings[REVIEWER_API_ERROR_CODES.noEventData],
            });
          }
        }
      },
      (error) => {
        toast.error(LocalStrings.static.kWrongCred);
        callback({
          success: 0,
          data: null,
          error: LocalStrings.static.kWrongCred,
        });
      }
    );
  };
};

export const reviewerLogoutAction = (session, callback) => {
  let url = `${INSTANCE_URL}/api/v2/user/session`;

  return (dispatch) => {
    deleteRequest(
      url,
      session,
      dispatch,
      (res) => {
        dispatch({ type: REVIEWER_LOGOUT });
        callback({ success: 1, error: null });
      },
      (error) => {
        callback({ success: 0, error });
      }
    );
  };
};

export const fetchReviewerData = (
  session,
  extra = { dfId: null, eventid: null },
  callback
) => {
  if (_.isNil(extra?.dfId) || _.isNil(extra?.eventid)) return (dispatch) => {};

  let postUrl = `${INSTANCE_URL}/api/v2/userregistration`;
  let postBody = _.cloneDeep(REVIEWER_API_PAYLOAD_STRUCTURES.fetchReviewer);
  postBody.dfid = extra?.dfId;
  postBody.eventid = extra?.eventid;

  return (dispatch) => {
    postRequest(
      postUrl,
      postBody,
      session,
      dispatch,
      (res) => {
        callback({
          success: 1,
          userFound: !_.isEmpty(res?.resource),
          data: !_.isEmpty(res?.resource) ? res?.resource?.[0] : res,
          error: null,
        });
      },
      (error) => {
        callback({ success: 0, data: null, error });
      }
    );
  };
};

export const fetchReviewerResponses = (session, callback = null) => {
  let postUrl = `${INSTANCE_URL}/api/v2/userregistration`;
  let postBody = _.cloneDeep(REVIEWER_API_PAYLOAD_STRUCTURES.reviewerResponses);
  postBody.reviewerid = session?.userid;
  postBody.eventid = session?.eventid;

  return (dispatch) => {
    postRequest(
      postUrl,
      postBody,
      session,
      dispatch,
      (res) => {
        let payload = _.cloneDeep(REVIEWER_REDUCER_STRUCTURES.reviewResponses);
        if (!_.isEmpty(res?.resource)) {
          payload[REVIEWER_RESPONSE_TYPES.unReviewed] = _.chain(res?.resource)
            .filter((response) =>
              _.isEqual(response?.status, REVIEWER_RESPONSE_TYPES.unReviewed)
            )
            .orderBy(["submission_date"], ["desc"])
            .value();
          payload[REVIEWER_RESPONSE_TYPES.reviewed] = _.chain(res?.resource)
            .filter((response) =>
              _.isEqual(response?.status, REVIEWER_RESPONSE_TYPES.reviewed)
            )
            .orderBy(["reviewed_date"], ["desc"])
            .value();
        }

        dispatch({ type: REVIEWER_RESPONSES, payload });
        callback && callback({ success: 1, data: payload, error: null });

        dispatch(fetchAllReviewerCriteria(session));
        dispatch(fetchReviewerAllReviews(session));
      },
      (error) => {
        toast.error(LocalStrings.kSomethingWentWrongMsg);
        callback && callback({ success: 0, data: null, error });
      }
    );
  };
};

export const fetchAllReviewerCriteria = (session, callback = null) => {
  let postUrl = `${INSTANCE_URL}/api/v2/userregistration`;
  let postBody = _.cloneDeep(
    REVIEWER_API_PAYLOAD_STRUCTURES.reviewerAllCriteria
  );
  postBody.eventid = session?.eventid;

  return (dispatch) => {
    postRequest(
      postUrl,
      postBody,
      session,
      dispatch,
      (res) => {
        let payload = [];
        if (!_.isEmpty(res?.resource)) payload = res?.resource;
        dispatch({ type: REVIEWER_ALL_CRITERIA, payload });
        callback && callback({ success: 1, data: payload, error: null });
      },
      (error) => {
        toast.error(LocalStrings.kSomethingWentWrongMsg);
        callback && callback({ success: 0, data: null, error });
      }
    );
  };
};

export const fetchReviewerAllReviews = (session, callback = null) => {
  let postUrl = `${INSTANCE_URL}/api/v2/userregistration`;
  let postBody = _.cloneDeep(
    REVIEWER_API_PAYLOAD_STRUCTURES.reviewerAllReviews
  );
  postBody.eventid = session?.eventid;

  return (dispatch) => {
    postRequest(
      postUrl,
      postBody,
      session,
      dispatch,
      (res) => {
        let payload = [];
        if (!_.isEmpty(res?.resource)) payload = res?.resource;
        dispatch({ type: REVIEWER_ALL_REVIEW, payload });
        callback && callback({ success: 1, data: payload, error: null });
      },
      (error) => {
        toast.error(LocalStrings.kSomethingWentWrongMsg);
        callback && callback({ success: 0, data: null, error });
      }
    );
  };
};

/**
 * @param extra.type enum 'reject' | 'resubmit'
 */
export const rejectProposal = (
  session,
  extra = { responseId: 0, comment: null, type: "reject" },
  callback = null
) => {
  if (_.isNil(extra?.responseId) || _.isEmpty(extra?.type))
    return (dispatch) => {
      toast.error(LocalStrings.kSomethingWentWrongMsg);
      console.error("Response id/type is missing.");
      callback({ success: 0, data: null, error: null });
    };

  let patchUrl = `${INSTANCE_URL}/api/v2/eventify/_table/proposal_response/${extra?.responseId}`;
  let patchBody = {
    status: PROPOSAL_SUBMISSION_TYPES[extra?.type].status,
    markas: PROPOSAL_SUBMISSION_TYPES[extra?.type].mark,
    markas_date: format(
      utcToZonedTime(new Date(), "UTC"),
      "yyyy-MM-dd HH:mm:ss"
    ),
    comment: !_.isEmpty(extra?.comment) ? extra?.comment : null,
  };

  return (dispatch) => {
    patchRequest(
      patchUrl,
      patchBody,
      session,
      dispatch,
      (patchRes) => {
        toast.success(LocalStrings.kThankYouSupportTicketSubmit);
        dispatch(
          triggerResubmissionMail(session, extra, (res) => {
            if (res?.success) {
              toast.success(LocalStrings.kEmailInitiated);
              callback &&
                callback({
                  success: 1,
                  data: { email: res?.data, proposal: patchRes },
                  error: null,
                });
            } else {
              toast.error(LocalStrings.static.kEmailNotInitiated);
              callback &&
                callback({ success: 2, data: patchRes, error: res?.error });
            }
          })
        );
      },
      (patchError) => {
        toast.error(LocalStrings.kSomethingWentWrongMsg);
        callback && callback({ success: 0, data: null, error: patchError });
      }
    );
  };
};

const triggerResubmissionMail = (
  session,
  extra = { responseId: 0, comment: null },
  callback
) => {
  let postUrl = `${INSTANCE_URL}/api/v2/proposal_resubmission`;
  let postBody = _.cloneDeep(
    REVIEWER_API_PAYLOAD_STRUCTURES.proposalResubmission
  );
  postBody.eventid = session?.eventid;
  postBody.id = extra?.responseId;
  postBody.comment = !_.isEmpty(extra?.comment) ? extra?.comment : null;

  return (dispatch) => {
    postRequest(
      postUrl,
      postBody,
      session,
      dispatch,
      (res) => {
        callback({ success: 1, data: res, error: null });
      },
      (error) => {
        toast.error(LocalStrings.kSomethingWentWrongMsg);
        callback({ success: 0, data: null, error });
      }
    );
  };
};

export const addProposalReview = (session, values, callback) => {
  if (_.isEmpty(values)) return (dispatch) => {};

  let postUrl = `${INSTANCE_URL}/api/v2/eventify/_table/proposal_review`;
  let postBody = { resource: values };

  return (dispatch) => {
    postRequest(
      postUrl,
      postBody,
      session,
      dispatch,
      (res) => {
        callback({ success: 1, data: res, error: null });
      },
      (error) => {
        toast.error(LocalStrings.kSomethingWentWrongMsg);
        callback({ success: 0, data: null, error });
      }
    );
  };
};

export const markResponseReviewed = (
  session,
  extra = { responseId: null, avgRating: 0, comment: null },
  callback
) => {
  let patchUrl = `${INSTANCE_URL}/api/v2/eventify/_table/proposal_response/${extra?.responseId}`;
  let patchBody = {
    status: REVIEWER_RESPONSE_TYPES.reviewed,
    avg_rating: extra?.avgRating,
    comment: extra?.comment,
  };

  return (dispatch) => {
    patchRequest(
      patchUrl,
      patchBody,
      session,
      dispatch,
      (res) => {
        toast.success(LocalStrings.kThankYouSupportTicketSubmit);
        callback({ success: 1, data: res, error: null });
      },
      (error) => {
        toast.error(LocalStrings.kSomethingWentWrongMsg);
        callback({ success: 0, data: null, error });
      }
    );
  };
};
